import { AspectRatio, Image } from '@mantine/core'
import { IconSquareArrowDown, IconSquareArrowUp } from '@tabler/icons-react'
import { Typography } from '../../common/typography'
import classes from './KpiAchievementContribution.module.css'
import { getRankSuffix } from '../../common/helpers'
import { useEffect, useState, useRef } from 'react'
import clsx from 'clsx'
import { Fmt, FmtColored } from '../../widgets/format'

export type KpiAchievementContributionData = {
  metric: string
  achievement: number
  contribution: number
  contribution_percent: number
  achievement_rank: number
  contribution_rank: number
  team_name: string
  team_logo: string
}

export type OrderField = 'achievement' | 'contribution'
export type OrderDirection = 'asc' | 'desc'
export type Order = { field: OrderField; direction: OrderDirection }

function AnimatedProgress({
  value,
  direction = 'ltr',
}: {
  value: number
  direction?: 'ltr' | 'rtl'
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const height = 8
  const radius = height / 2
  const [svgWidth, setSvgWidth] = useState(100)

  useEffect(() => {
    const updateWidth = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect()
        if (rect.width > 0) {
          setSvgWidth(rect.width)
        }
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const width = svgWidth
  const progressWidth = Math.min(value * width, width)
  const isComplete = value >= 1

  const [animatedWidth, setAnimatedWidth] = useState(0)

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedWidth(progressWidth)
    }, 200)
    return () => clearTimeout(timer)
  }, [progressWidth])

  const hasSpaceForCut = animatedWidth < width * 0.9

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      className={classes.progressSvg}
      preserveAspectRatio="none"
    >
      <path
        d={
          direction === 'ltr'
            ? `
          M 0 0
          L ${width - radius} 0
          Q ${width} 0 ${width} ${radius}
          L ${width} ${height - radius}
          Q ${width} ${height} ${width - radius} ${height}
          L 0 ${height}
          Z
        `
            : `
          M ${radius} 0
          L ${width} 0
          L ${width} ${height}
          L ${radius} ${height}
          Q 0 ${height} 0 ${height - radius}
          L 0 ${radius}
          Q 0 0 ${radius} 0
          Z
        `
        }
        className={classes.backgroundBar}
      />
      {direction === 'ltr' ? (
        <path
          d={
            isComplete || !hasSpaceForCut
              ? `
              M 0 0
              L ${animatedWidth - radius} 0
              Q ${animatedWidth} 0 ${animatedWidth} ${radius}
              L ${animatedWidth} ${height - radius}
              Q ${animatedWidth} ${height} ${animatedWidth - radius} ${height}
              L 0 ${height}
              Z
            `
              : `
              M 0 0
              L ${animatedWidth} 0
              L ${animatedWidth} ${height}
              L 0 ${height}
              Z
            `
          }
          className={clsx(classes.progressBar, classes.black)}
        />
      ) : (
        <path
          d={
            isComplete || !hasSpaceForCut
              ? `
              M ${width} 0
              L ${width - animatedWidth + radius} 0
              Q ${width - animatedWidth} 0 ${width - animatedWidth} ${radius}
              L ${width - animatedWidth} ${height - radius}
              Q ${width - animatedWidth} ${height} ${width - animatedWidth + radius} ${height}
              L ${width} ${height}
              Z
            `
              : `
              M ${width} 0
              L ${width - animatedWidth} 0
              L ${width - animatedWidth} ${height}
              L ${width} ${height}
              Z
            `
          }
          className={clsx(classes.progressBar, {
            [classes.orange]: value > 0.7 && value < 1,
            [classes.red]: value <= 0.7,
          })}
        />
      )}
    </svg>
  )
}

export default function KpiAchievementContribution({
  data,
  order,
  onOrderChange,
  isPlayer,
}: {
  data: KpiAchievementContributionData[]
  order: Order
  onOrderChange: (order: Order) => void
  isPlayer?: boolean
}) {
  const handleHeaderClick = (field: OrderField) => {
    if (order.field === field) {
      onOrderChange({
        field,
        direction: order.direction === 'asc' ? 'desc' : 'asc',
      })
    } else {
      onOrderChange({ field, direction: 'asc' })
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div
          className={classes.headerRow}
          onClick={() => handleHeaderClick('achievement')}
        >
          <Typography fs="sm" fw={600} lh="140">
            Achievement
          </Typography>
          {order.field === 'achievement' &&
            (order.direction === 'desc' ? (
              <IconSquareArrowDown className={classes.icon} />
            ) : (
              <IconSquareArrowUp className={classes.icon} />
            ))}
        </div>
        <div
          className={classes.headerRow}
          onClick={() => handleHeaderClick('contribution')}
        >
          <Typography fs="sm" fw={600} lh="140">
            Contribution
          </Typography>
          {order.field === 'contribution' &&
            (order.direction === 'desc' ? (
              <IconSquareArrowDown className={classes.icon} />
            ) : (
              <IconSquareArrowUp className={classes.icon} />
            ))}
        </div>
      </div>
      <div className={classes.rows}>
        {data.map((item, index) => (
          <div key={index} className={classes.item}>
            <div className={classes.row}>
              <Typography
                fw={500}
                ff="JetBrains Mono"
                lh="150"
                className={classes.rowItem}
              >
                <FmtColored
                  metric={item.metric}
                  component="percent"
                  statistic="value"
                  value={item.achievement / 100}
                />
              </Typography>
              <Typography
                fw={500}
                lh="125"
                ta="center"
                className={classes.rowItem}
              >
                {item.team_name}
              </Typography>
              <Typography
                fw={500}
                ff="JetBrains Mono"
                lh="150"
                ta="right"
                className={classes.rowItem}
              >
                <Fmt
                  metric={item.metric}
                  component="actual"
                  statistic="value"
                  value={item.contribution}
                />
              </Typography>
            </div>
            <div className={classes.row}>
              <AnimatedProgress
                value={item.achievement / 100}
                direction="rtl"
              />
              <Typography fs="xl" fw={700} lh="120">
                {item.achievement_rank > 0 ? (
                  <>
                    {item.achievement_rank}
                    <sup>{getRankSuffix(item.achievement_rank)}</sup>
                  </>
                ) : (
                  '-'
                )}
              </Typography>
              <AspectRatio ratio={1 / 1} className={classes.logoContainer}>
                <Image
                  src={item.team_logo}
                  alt={item.team_name}
                  className={classes.teamLogo}
                  fit="scale-down"
                  data-player={isPlayer}
                />
              </AspectRatio>
              <Typography fs="xl" fw={700} lh="120">
                {item.contribution_rank > 0 ? (
                  <>
                    {item.contribution_rank}
                    <sup>{getRankSuffix(item.contribution_rank)}</sup>
                  </>
                ) : (
                  '-'
                )}
              </Typography>
              <AnimatedProgress
                value={item.contribution_percent}
                direction="ltr"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
